// The badge engine switch: the `badge-engine` scheduler task. It gates the
// instant checks, the race step and the 05:30 UTC sweep together.
import React from "react";
import { toast } from "react-toastify";
import { useConfirm } from "../../../ui/components/ConfirmDialog";
import {
  useGetSchedulerStatusQuery,
  useToggleSchedulerTaskMutation,
} from "../../../services/rtkApi/schedulerAdminApi";
import { BADGE_ENGINE_TASK } from "./adminBadgesApi";

/**
 * "the daily sweep at 05:30 UTC" for a daily "30 5 * * *" cron (the prod
 * server runs in UTC), "the scheduled sweep (cron ...)" for anything else.
 */
export function sweepWhen(cron: string | undefined): string {
  const c = (cron ?? "30 5 * * *").trim();
  const m = /^(\d{1,2}) (\d{1,2}) \* \* \*$/.exec(c);
  if (!m) return `the scheduled sweep (cron ${c})`;
  return `the daily sweep at ${m[2].padStart(2, "0")}:${m[1].padStart(2, "0")} UTC`;
}

const EngineStatusPanel: React.FC = () => {
  const { data, isLoading, isError, refetch } = useGetSchedulerStatusQuery();
  const [toggleTask, { isLoading: saving }] = useToggleSchedulerTaskMutation();
  const confirm = useConfirm();

  const task = data?.tasks?.find((t) => t.name === BADGE_ENGINE_TASK);
  const on = !!task?.enabled;
  const when = sweepWhen(task?.cron);

  const flip = async (next: boolean) => {
    if (next) {
      const ok = await confirm({
        title: "Turn the badge engine on?",
        message:
          "Members start earning badges on their own: right after they chat, race or play a card, and in the daily sweep. Run the seed, the migration and a backfill sweep first, or this week's history arrives as fresh reveals.",
        confirmLabel: "Turn it on",
      });
      if (!ok) return;
    }
    try {
      await toggleTask({ taskName: BADGE_ENGINE_TASK, enabled: next }).unwrap();
      toast.success(next ? "The badge engine is on." : "The badge engine is off.");
    } catch (error) {
      console.error("Badge engine toggle failed:", error);
      toast.error("Could not change the badge engine switch.");
    }
  };

  let body: React.ReactNode;
  if (isLoading) {
    body = <p className="badm-muted">Loading the scheduler status</p>;
  } else if (isError || !data) {
    body = (
      <div className="badm-inline-row">
        <p className="badm-muted">Could not load the scheduler status.</p>
        <button type="button" className="badm-btn badm-btn--sm" onClick={() => refetch()}>
          Try again
        </button>
      </div>
    );
  } else if (!task) {
    body = (
      <p className="badm-note badm-note--warn">
        This backend has no <code>badge-engine</code> task yet. Deploy the badges-3 backend first.
      </p>
    );
  } else {
    body = (
      <>
        <div className="badm-engine">
          <div className="badm-engine__control">
            <span className={`badm-state badm-state--${on ? "on" : "off"}`} data-testid="badm-engine-state">
              {on ? "On" : "Off"}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={on}
              aria-label="Badge engine"
              className={`badm-switch${on ? " badm-switch--on" : ""}`}
              disabled={saving}
              onClick={() => void flip(!on)}
            >
              <span className="badm-switch__thumb" aria-hidden="true" />
            </button>
            <span className="badm-engine__status">
              {saving ? "Saving" : on ? "Awarding badges on its own" : "Not awarding anything on its own"}
            </span>
          </div>
          <p className="badm-engine__line">
            The switch gates the instant checks (chat, race, cards), the badge step after each race settles, and{" "}
            {when}. The sweep below runs either way.
          </p>
        </div>
        {!data.schedulerGloballyEnabled && (
          <p className="badm-note badm-note--warn">
            The scheduler is off on this server (SCHEDULER_ENABLED is not true), so {when} will not fire. The instant
            checks and the race step still follow this switch.
          </p>
        )}
      </>
    );
  }

  return (
    <section className="badm-panel" aria-labelledby="badm-engine-title">
      <header className="badm-panel__head" style={{ background: on ? "#60fac1" : "#e4e4e4" }}>
        <h2 id="badm-engine-title" className="badm-panel__title">
          Badge engine
        </h2>
        <span className="badm-panel__aside">automatic awarding</span>
      </header>
      <div className="badm-panel__body">{body}</div>
    </section>
  );
};

export default EngineStatusPanel;
